// Sprint DS A12: Focus Mode — Doc 35 §3
// Global toggle (Cmd+.): hides sidebar, fades non-essential, expands content
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface FocusModeState {
  /** Whether focus mode is active */
  isActive: boolean;
  /** Toggle focus mode on/off */
  toggle: () => void;
  /** Activate focus mode */
  activate: () => void;
  /** Deactivate focus mode */
  deactivate: () => void;
}

const FocusModeContext = createContext<FocusModeState>({
  isActive: false,
  toggle: () => {},
  activate: () => {},
  deactivate: () => {},
});

export function useFocusMode(): FocusModeState {
  return useContext(FocusModeContext);
}

export function useFocusModeValue(): FocusModeState {
  const [isActive, setIsActive] = useState(false);

  const toggle = useCallback(() => setIsActive((prev) => !prev), []);
  const activate = useCallback(() => setIsActive(true), []);
  const deactivate = useCallback(() => setIsActive(false), []);

  // Global keyboard shortcut: Cmd+. (Mac) or Ctrl+. (Windows)
  // deactivate() is a no-op when already inactive, so no isActive check needed
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ".") {
        e.preventDefault();
        toggle();
      }
      if (e.key === "Escape") {
        deactivate();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle, deactivate]);

  // Apply focus-mode data attribute to document for CSS targeting
  useEffect(() => {
    if (isActive) {
      document.documentElement.setAttribute("data-focus-mode", "true");
    } else {
      document.documentElement.removeAttribute("data-focus-mode");
    }
  }, [isActive]);

  return { isActive, toggle, activate, deactivate };
}

export function FocusModeProvider({ children }: { children: ReactNode }) {
  const value = useFocusModeValue();
  const memoizedValue = useMemo(() => value, [value]);

  return createElement(FocusModeContext.Provider, { value: memoizedValue }, children);
}
